/** SUNTRONIX ONE — Admin Demo Console (Tenant Pilot & Client-Ready Certification).
 *  Super-admin only. Seed/reset the DEMO tenant, view Client Demo Readiness,
 *  run the Pre-Demo Check, and read the guided Demo Script.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Database, ShieldCheck, PlayCircle, RotateCcw, Loader2, CheckCircle2,
  AlertTriangle, XCircle, ClipboardList, Copy,
} from "lucide-react";

const STATUS_ICON = {
  PASS: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  WARNING: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  FAIL: <XCircle className="h-4 w-4 text-rose-600" />,
};

export default function DemoConsole() {
  const [status, setStatus] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [preCheck, setPreCheck] = useState(null);
  const [script, setScript] = useState(null);
  const [busy, setBusy] = useState("");

  const loadAll = useCallback(() => {
    api.get("/demo/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/demo/readiness").then(({ data }) => setReadiness(data)).catch(() => {});
    api.get("/demo/pre-check").then(({ data }) => setPreCheck(data)).catch(() => {});
    api.get("/demo/script").then(({ data }) => setScript(data)).catch(() => {});
  }, []);
  useEffect(loadAll, [loadAll]);

  const run = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/demo/${kind}`);
      toast.success(kind === "seed" ? "Demo tenant seeded ✓" : "Demo tenant reset to a clean state ✓");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="demo-console">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-amber-500 grid place-items-center text-white"><Sparkles className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Demo Console</h1>
          <p className="text-[13px] text-slate-500">Tenant pilot & client-ready certification — seed, certify and demo with confidence.</p>
        </div>
      </div>

      {/* Seed / reset */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-tenant-card">
        <div className="flex flex-wrap items-center gap-3">
          <Database className="h-5 w-5 text-slate-400" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[13px] font-semibold text-slate-800">{status?.name || "SUNTRONIX ONE DEMO"}</div>
            <div className="text-[11px] text-slate-500">{status?.seeded
              ? Object.entries(status.counts).map(([k, v]) => `${v} ${k}`).join(" · ")
              : "Not seeded yet"}</div>
          </div>
          <Button variant="outline" disabled={!!busy} onClick={() => run("seed")} data-testid="demo-seed">
            {busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlayCircle className="h-4 w-4 mr-1.5" />}Seed demo
          </Button>
          {status?.seeded && (
            <Button variant="outline" disabled={!!busy} onClick={() => run("reset")} data-testid="demo-reset" className="text-amber-700 border-amber-200 hover:bg-amber-50">
              {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}Reset demo
            </Button>
          )}
        </div>
        {status?.demo_users && (
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="demo-users">
            {status.demo_users.map((u) => (
              <button key={u.email} onClick={() => copy(`${u.email} / ${u.password}`)}
                className="text-left rounded-lg border border-slate-100 bg-slate-50 p-2 hover:bg-slate-100">
                <div className="text-[11px] font-medium text-slate-700 truncate flex items-center gap-1"><Copy className="h-3 w-3 text-slate-400" />{u.email}</div>
                <div className="text-[10px] text-slate-400">{u.role} · {u.password}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Readiness */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[13px] font-semibold text-slate-700 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Client Demo Readiness</h2>
            {readiness && <span className={"text-[11px] font-bold px-2 py-0.5 rounded-full " + (readiness.overall === "CLIENT DEMO READY" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200")} data-testid="readiness-overall">{readiness.overall}</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="readiness-list">
            {readiness?.sections?.map((s) => (
              <div key={s.section} className="flex items-center gap-2 px-3 py-2" data-testid={`readiness-${s.section}`}>
                {STATUS_ICON[s.status]}
                <span className="text-[13px] font-medium text-slate-800 w-28">{s.section}</span>
                <span className="text-[11px] text-slate-500 flex-1">{s.detail}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pre-demo check */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[13px] font-semibold text-slate-700 flex items-center gap-1.5"><ClipboardList className="h-4 w-4" /> Pre-Demo Check</h2>
            {preCheck && <span className={"text-[11px] font-bold " + (preCheck.ready ? "text-emerald-600" : "text-rose-600")} data-testid="precheck-ready">{preCheck.ready ? "READY" : "ACTION REQUIRED"}</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="precheck-list">
            {preCheck?.checks?.map((c) => (
              <div key={c.section} className="flex items-center gap-2 px-3 py-2">
                {STATUS_ICON[c.status]}
                <span className="text-[13px] text-slate-800 flex-1">{c.section}</span>
                {c.detail && <span className="text-[10px] text-slate-400">{c.detail}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Demo script */}
      {script && (
        <div>
          <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Guided Demo Script</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="script-flow">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Suggested flow</div>
              {script.flow.map((f) => (
                <div key={f.step} className="flex gap-2 py-1 text-[12px]">
                  <span className="h-5 w-5 shrink-0 rounded-full bg-slate-900 text-white grid place-items-center text-[10px] font-bold">{f.step}</span>
                  <span className="font-medium text-slate-800">{f.title}</span>
                  <span className="text-slate-400">— {f.do}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="script-questions">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Ask ONE — demo questions</div>
              {script.ask_one_questions.map((qn, i) => (
                <button key={i} onClick={() => copy(qn)} className="block w-full text-left text-[12px] text-slate-600 hover:text-indigo-700 py-0.5 truncate">“{qn}”</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
